import {
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type IToastProps,
    NCoreUIKitToast,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Toast";

const USAGE_CODE = `import {
    NCoreUIKitToast
} from "ncore-ui-kit";

NCoreUIKitToast.open({
    action: {
        onPress: () => restoreDraft(),
        title: "Undo"
    },
    title: "Draft deleted",
    isShowAction: true,
    side: "bottom",
    align: "center"
});`;

const ACTION_CODE = `NCoreUIKitToast.open({
    action: {
        onPress: ({ closeAnimation }) => closeAnimation({
            onClosed: () => retryConnection()
        }),
        title: "Retry"
    },
    isCloseOnPressActionButton: false,
    title: "Connection failed",
    isShowAction: true,
    type: "danger"
});`;

const LOADING_CODE = `const id = NCoreUIKitToast.open({
    title: "Uploading report.pdf…",
    isCloseOnPress: false,
    isLoading: true
});

NCoreUIKitToast.close({
    id
});`;

const SECONDARY_CODE = `NCoreUIKitToast.open({
    action: {
        onPress: () => restoreTask(),
        title: "Undo"
    },
    secondaryAction: {
        title: "Dismiss"
    },
    subTitle: "“Review the report” is no longer in the list.",
    title: "Task moved to the archive",
    isShowAction: true
});`;

const PROMISE_CODE = `const id = NCoreUIKitToast.promise(createReport(), {
    success: (report) => ({
        action: {
            onPress: () => openReport(report),
            title: "Open"
        },
        subTitle: \`\${report.name} · \${report.size}\`,
        title: "Report is ready",
        isShowAction: true
    }),
    error: {
        action: {
            onPress: () => retryReport(),
            title: "Retry"
        },
        title: "Report could not be created",
        isShowAction: true
    },
    loading: "Preparing the report…",
    side: "bottom"
});`;

const LIST_CODE = `NCoreUIKitToast.open({
    subTitle: "The October report was shared with 3 people.",
    action: {
        onPress: () => openReport(),
        title: "View"
    },
    title: "Report sent",
    stackMode: "list",
    isCompact: false,
    type: "success",
    side: "top"
});`;

const PRESS_CODE = `NCoreUIKitToast.open({
    onPress: ({ id }) => openMessage(id),
    title: "New message from Ayşe",
    edgeOffset: 56
});`;

const UPLOAD_DELAY = 2000;

const STACK_DELAY = 250;

const PROMISE_DELAY = 2500;

type ToastTypeValue = NonNullable<IToastProps["type"]>;

type ToastPositionValue = Required<Pick<IToastProps, "align" | "side">>;

type ToastClosingSample = {
    props: Pick<IToastProps, "autoCloseDelay" | "isAutoClose" | "isCloseOnPress">;
    messageKey: string;
    labelKey: string;
};

const TYPES: Array<ToastTypeValue> = [
    "primary",
    "neutral",
    "success",
    "info",
    "warning",
    "danger"
];

const STACK_TYPES: Array<ToastTypeValue> = [
    "neutral",
    "success",
    "info"
];

const TYPE_MESSAGES: Record<ToastTypeValue, string> = {
    neutral: "toastPage.messageNeutral",
    primary: "toastPage.messagePrimary",
    success: "toastPage.messageSuccess",
    warning: "toastPage.messageWarning",
    danger: "toastPage.messageDanger",
    info: "toastPage.messageInfo"
};

const POSITIONS: Array<ToastPositionValue> = [
    {
        align: "start",
        side: "bottom"
    },
    {
        align: "center",
        side: "bottom"
    },
    {
        side: "bottom",
        align: "end"
    },
    {
        align: "start",
        side: "top"
    },
    {
        align: "center",
        side: "top"
    },
    {
        align: "end",
        side: "top"
    }
];

const CLOSING_SAMPLES: Array<ToastClosingSample> = [
    {
        messageKey: "toastPage.shortDelayMessage",
        labelKey: "toastPage.shortDelay",
        props: {
            autoCloseDelay: 2000
        }
    },
    {
        messageKey: "toastPage.stayOpenMessage",
        labelKey: "toastPage.stayOpen",
        props: {
            isAutoClose: false
        }
    },
    {
        messageKey: "toastPage.pressOffMessage",
        labelKey: "toastPage.pressOff",
        props: {
            isCloseOnPress: false,
            autoCloseDelay: 4000
        }
    }
];

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("toastPage.typesTitle"),
        id: "types"
    },
    {
        title: t("toastPage.positionTitle"),
        id: "position"
    },
    {
        title: t("toastPage.actionsTitle"),
        id: "actions"
    },
    {
        title: t("toastPage.secondaryTitle"),
        id: "secondary-action"
    },
    {
        title: t("toastPage.listTitle"),
        id: "list"
    },
    {
        title: t("toastPage.pressTitle"),
        id: "press"
    },
    {
        title: t("toastPage.loadingTitle"),
        id: "loading"
    },
    {
        title: t("toastPage.promiseTitle"),
        id: "promise"
    },
    {
        title: t("toastPage.closingTitle"),
        id: "closing"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ToastPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isDraftDeleted,
        setIsDraftDeleted
    ] = useState(false);

    const [
        pressCount,
        setPressCount
    ] = useState(0);

    const [
        uploadToastId,
        setUploadToastId
    ] = useState<string | undefined>(undefined);

    useEffect(() => {
        if(!uploadToastId) {
            return;
        }

        const timer = window.setTimeout(() => {
            NCoreUIKitToast.open({
                title: t("toastPage.uploaded", [
                    t("sample.fileName")
                ]),
                type: "success"
            });

            setUploadToastId(undefined);
        }, UPLOAD_DELAY);

        return () => {
            window.clearTimeout(timer);

            NCoreUIKitToast.close({
                id: uploadToastId
            });
        };
    }, [
        uploadToastId
    ]);

    const handleUndo = () => setIsDraftDeleted(false);

    const handleDraftDelete = () => {
        setIsDraftDeleted(true);

        NCoreUIKitToast.open({
            title: t("toastPage.draftDeleted"),
            action: {
                title: t("toastPage.undo"),
                onPress: handleUndo
            },
            isShowAction: true
        });
    };

    const handleTypeOpen = (type: ToastTypeValue) => {
        NCoreUIKitToast.open({
            title: t(TYPE_MESSAGES[type]),
            type
        });
    };

    const handleStackOpen = () => {
        STACK_TYPES.forEach((type, index) => {
            window.setTimeout(() => handleTypeOpen(type), index * STACK_DELAY);
        });
    };

    const handlePositionOpen = ({
        align,
        side
    }: ToastPositionValue) => {
        NCoreUIKitToast.open({
            title: t("toastPage.positionMessage"),
            align,
            side
        });
    };

    const handleActionOpen = () => {
        NCoreUIKitToast.open({
            title: t("toastPage.messageDanger"),
            action: {
                title: t("toastPage.retry"),
                onPress: () => undefined
            },
            isShowAction: true,
            type: "danger"
        });
    };

    const handleCloseOpen = () => {
        NCoreUIKitToast.open({
            title: t("toastPage.reportSent"),
            isShowClose: true,
            type: "success"
        });
    };

    const handleBothOpen = () => {
        NCoreUIKitToast.open({
            action: {
                title: t("toastPage.undo"),
                onPress: () => undefined
            },
            title: t("toastPage.archived"),
            isShowAction: true,
            isShowClose: true
        });
    };

    const handleSecondaryOpen = () => {
        NCoreUIKitToast.open({
            subTitle: t("toastPage.taskArchivedBody"),
            secondaryAction: {
                title: t("toastPage.secondaryClose")
            },
            title: t("toastPage.taskArchived"),
            action: {
                title: t("toastPage.undo"),
                onPress: () => undefined
            },
            isShowAction: true
        });
    };

    const handlePromiseOpen = (isFailing: boolean) => {
        const report = new Promise<string>((resolve, reject) => {
            window.setTimeout(() => {
                if(isFailing) {
                    reject(new Error(t("toastPage.reportFailed")));

                    return;
                }

                resolve(t("toastPage.reportReadyBody"));
            }, PROMISE_DELAY);
        });

        NCoreUIKitToast.promise(report, {
            error: {
                action: {
                    title: t("toastPage.reportRetry"),
                    onPress: () => undefined
                },
                title: t("toastPage.reportFailed"),
                isShowAction: true
            },
            success: (details) => ({
                action: {
                    title: t("toastPage.reportOpen"),
                    onPress: () => undefined
                },
                title: t("toastPage.reportReady"),
                isShowAction: true,
                subTitle: details
            }),
            loading: t("toastPage.reportPreparing")
        });
    };

    const handleListOpen = () => {
        NCoreUIKitToast.open({
            action: {
                title: t("toastPage.listView"),
                onPress: () => undefined
            },
            subTitle: t("toastPage.listBody"),
            title: t("toastPage.listReport"),
            stackMode: "list",
            isCompact: false,
            type: "success",
            side: "top"
        });
    };

    const handleListFullOpen = () => {
        NCoreUIKitToast.open({
            title: t("toastPage.listWide"),
            spreadBehaviour: "stretch",
            stackMode: "list",
            isCompact: false,
            type: "info",
            side: "top"
        });
    };

    const handlePressOpen = () => {
        NCoreUIKitToast.open({
            onPress: () => setPressCount((count) => count + 1),
            title: t("toastPage.pressMessage"),
            isCloseOnPress: false,
            edgeOffset: 56
        });
    };

    const handleUploadOpen = () => {
        setUploadToastId(NCoreUIKitToast.open({
            title: t("toastPage.uploading", [
                t("sample.fileName")
            ]),
            isCloseOnPress: false,
            isLoading: true
        }));
    };

    const handleClosingOpen = (sample: ToastClosingSample) => {
        NCoreUIKitToast.open({
            ...sample.props,
            title: t(sample.messageKey)
        });
    };

    return <Article>
        <ComponentHeader
            lead={t("toastPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("toastPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("toastPage.overviewCodeText")}
            </Paragraph>
            <Preview
                caption={t("toastPage.overviewCaption")}
            >
                <Button
                    title={t("toastPage.deleteDraft")}
                    onPress={handleDraftDelete}
                    variant="outline"
                    type="neutral"
                />
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t(isDraftDeleted ? "toastPage.statusDeleted" : "toastPage.statusKept")}
                </Text>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
            <Paragraph>
                {t("toastPage.overviewStackText")}
            </Paragraph>
            <Preview
                caption={t("toastPage.stackCaption")}
            >
                <Button
                    title={t("toastPage.openThree")}
                    onPress={handleStackOpen}
                    variant="outline"
                    type="neutral"
                />
            </Preview>
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("toastPage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("toastPage.typesText")}
            </Paragraph>
            <Preview>
                {
                    TYPES.map((type) => <Button
                        onPress={() => handleTypeOpen(type)}
                        variant="outline"
                        type="neutral"
                        title={type}
                        key={type}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("toastPage.positionTitle")}
            id="position"
        >
            <Paragraph>
                {t("toastPage.positionText")}
            </Paragraph>
            <Preview>
                {
                    POSITIONS.map((position) => <Button
                        title={`${position.side} · ${position.align}`}
                        onPress={() => handlePositionOpen(position)}
                        key={`${position.side}-${position.align}`}
                        variant="outline"
                        type="neutral"
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("toastPage.actionsTitle")}
            id="actions"
        >
            <Paragraph>
                {t("toastPage.actionsText")}
            </Paragraph>
            <Preview>
                <Button
                    title={t("toastPage.withAction")}
                    onPress={handleActionOpen}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("toastPage.withClose")}
                    onPress={handleCloseOpen}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("toastPage.withBoth")}
                    onPress={handleBothOpen}
                    variant="outline"
                    type="neutral"
                />
            </Preview>
            <CodeBlock
                code={ACTION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("toastPage.secondaryTitle")}
            id="secondary-action"
        >
            <Paragraph>
                {t("toastPage.secondaryText")}
            </Paragraph>
            <Preview
                caption={t("toastPage.secondaryCaption")}
            >
                <Button
                    title={t("toastPage.twoButtons")}
                    onPress={handleSecondaryOpen}
                    variant="outline"
                    type="neutral"
                />
            </Preview>
            <CodeBlock
                code={SECONDARY_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("toastPage.listTitle")}
            id="list"
        >
            <Paragraph>
                {t("toastPage.listText")}
            </Paragraph>
            <Preview
                caption={t("toastPage.listCaption")}
            >
                <Button
                    title={t("toastPage.listOpen")}
                    onPress={handleListOpen}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("toastPage.listOpenWide")}
                    onPress={handleListFullOpen}
                    variant="outline"
                    type="neutral"
                />
            </Preview>
            <CodeBlock
                code={LIST_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("toastPage.pressTitle")}
            id="press"
        >
            <Paragraph>
                {t("toastPage.pressText")}
            </Paragraph>
            <Preview
                caption={t("toastPage.pressCaption")}
            >
                <Button
                    title={t("toastPage.pressOpen")}
                    onPress={handlePressOpen}
                    variant="outline"
                    type="neutral"
                />
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t("toastPage.pressStatus", [
                        String(pressCount)
                    ])}
                </Text>
            </Preview>
            <CodeBlock
                code={PRESS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("toastPage.loadingTitle")}
            id="loading"
        >
            <Paragraph>
                {t("toastPage.loadingText")}
            </Paragraph>
            <Preview
                caption={t("toastPage.loadingCaption")}
            >
                <Button
                    title={t("toastPage.uploadFile")}
                    onPress={handleUploadOpen}
                    variant="outline"
                    type="neutral"
                />
            </Preview>
            <CodeBlock
                code={LOADING_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("toastPage.promiseTitle")}
            id="promise"
        >
            <Paragraph>
                {t("toastPage.promiseText")}
            </Paragraph>
            <Preview
                caption={t("toastPage.promiseCaption")}
            >
                <Button
                    onPress={() => handlePromiseOpen(false)}
                    title={t("toastPage.createReport")}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    onPress={() => handlePromiseOpen(true)}
                    title={t("toastPage.createReportFail")}
                    variant="outline"
                    type="neutral"
                />
            </Preview>
            <CodeBlock
                code={PROMISE_CODE}
                language="tsx"
            />
            <PropsTable
                rows={[
                    {
                        description: t("toastPage.promiseOptionLoading"),
                        type: "string | { title, ...toast props }",
                        name: "loading"
                    },
                    {
                        description: t("toastPage.promiseOptionSuccess"),
                        type: "state | (data) => state",
                        name: "success"
                    },
                    {
                        description: t("toastPage.promiseOptionError"),
                        type: "state | (error) => state",
                        name: "error"
                    },
                    {
                        description: t("toastPage.promiseOptionShared"),
                        name: t("toastPage.promiseOptionSharedName"),
                        type: "side, align, stackMode, edgeOffset"
                    }
                ]}
            />
        </Section>
        <Section
            title={t("toastPage.closingTitle")}
            id="closing"
        >
            <Paragraph>
                {t("toastPage.closingText")}
            </Paragraph>
            <Preview>
                {
                    CLOSING_SAMPLES.map((sample) => <Button
                        onPress={() => handleClosingOpen(sample)}
                        title={t(sample.labelKey)}
                        key={sample.labelKey}
                        variant="outline"
                        type="neutral"
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("toastPage.a11yLive"),
                    t("toastPage.a11yProvider"),
                    t("toastPage.a11yKeyboard"),
                    t("toastPage.a11yTimer"),
                    t("toastPage.a11yLoading"),
                    t("toastPage.a11yFocus"),
                    t("toastPage.a11yIcon"),
                    t("toastPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("toastPage.propIsAlwaysExpanded"),
                        name: "isAlwaysExpanded",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("toastPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        description: t("toastPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("toastPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("toastPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("toastPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("toastPage.propSide"),
                        type: "\"bottom\" | \"top\"",
                        defaultValue: "\"bottom\"",
                        name: "side"
                    },
                    {
                        type: "\"start\" | \"center\" | \"end\"",
                        description: t("toastPage.propAlign"),
                        defaultValue: "\"center\"",
                        name: "align"
                    },
                    {
                        description: t("toastPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"free\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("toastPage.propStackMode"),
                        type: "\"deck\" | \"list\"",
                        defaultValue: "\"deck\"",
                        name: "stackMode"
                    },
                    {
                        description: t("toastPage.propIsCompact"),
                        defaultValue: "true",
                        name: "isCompact",
                        type: "boolean"
                    },
                    {
                        description: t("toastPage.propEdgeOffset"),
                        name: "edgeOffset",
                        defaultValue: "0",
                        type: "number"
                    },
                    {
                        description: t("toastPage.propOnPress"),
                        type: "({ id }) => void",
                        name: "onPress"
                    },
                    {
                        description: t("toastPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        type: "{ title, onPress({ closeAnimation }), icon, style }",
                        description: t("toastPage.propAction"),
                        name: "action"
                    },
                    {
                        type: "{ title, onPress({ closeAnimation }), icon, style }",
                        description: t("toastPage.propSecondaryAction"),
                        name: "secondaryAction"
                    },
                    {
                        description: t("toastPage.propIsShowAction"),
                        defaultValue: "false",
                        name: "isShowAction",
                        type: "boolean"
                    },
                    {
                        description: t("toastPage.propIsShowClose"),
                        name: "isShowClose",
                        type: "boolean"
                    },
                    {
                        description: t("toastPage.propIsCloseOnPressActionButton"),
                        name: "isCloseOnPressActionButton",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("toastPage.propIsAutoClose"),
                        defaultValue: "true",
                        name: "isAutoClose",
                        type: "boolean"
                    },
                    {
                        description: t("toastPage.propAutoCloseDelay"),
                        name: "autoCloseDelay",
                        type: "number"
                    },
                    {
                        description: t("toastPage.propIsCloseOnPress"),
                        name: "isCloseOnPress",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("toastPage.propIsCloseOnSwipe"),
                        name: "isCloseOnSwipe",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("toastPage.propOnClosed"),
                        type: "({ id }) => void",
                        name: "onClosed"
                    },
                    {
                        description: t("toastPage.propOpenAnimationDelay"),
                        name: "openAnimationDelay",
                        type: "number"
                    },
                    {
                        description: t("toastPage.propCloseAnimationDelay"),
                        name: "closeAnimationDelay",
                        type: "number"
                    },
                    {
                        description: t("toastPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("toastPage.propIsWorkWithPortal"),
                        name: "isWorkWithPortal",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("toastPage.propIsLiveRegionEnabled"),
                        name: "isLiveRegionEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("toastPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("toastPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("toastPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ToastPage;
